import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Button } from "@mui/material";
import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { useToken } from "../../../../../../server/auth/use-token";
import { useDialog } from "../../../../../../utils/hooks/use-dialog";
import { serviceURL } from "../../../../../../utils/service-url";
import { uuidv4 } from "../../../../../../utils/uuid";
import { usePublishDesignHuddleTemplateWithTags } from "../../../../../design-huddle/use-publish-template-with-tags";
import { copyImage } from "../../../../../design-huddle/utils";
import { LinkForm } from "../../../components/link-form/link-form";
import { INSERT_LINK_COMMAND } from "../../../types/commands";

import type { CreateAssetResult } from "../../../../../assets/file-upload";
import type { Asset } from "../../../../../assets/server/digital-asset-service";
import type { Link } from "../../../components/link-form/types";
import type { FC } from "react";

export const InsertAsLinkButton: FC<{
  asset: Pick<Asset, "project_id" | "content_type" | "public_url" | "mime_type"> | null;
  onClose: () => void;
  onInsert?: (url: string) => Promise<CreateAssetResult>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  disabled?: boolean;
}> = ({ asset, loading, onClose, onInsert, setLoading, disabled = false }) => {
  const getToken = useToken();
  const [editor] = useLexicalComposerContext();
  const linkDialog = useDialog<{ url: string }>();
  const publishTags = usePublishDesignHuddleTemplateWithTags();

  const handleInsertAssetAsLink = useCallback(async () => {
    setLoading(true);
    if (!asset?.public_url) return;

    if (asset?.content_type === "Uploaded File" || asset.content_type === "Video") {
      if (asset.public_url) {
        onInsert && (await onInsert(asset.public_url));
        linkDialog.handleOpen({ url: asset.public_url });
      }
      setLoading(false);
      return;
    }

    const template = asset.project_id && (await publishTags(asset.project_id));
    if (template) {
      const params =
        template.mergeTags.length > 0
          ? `?${template.mergeTags.map((tag) => `${tag}={{${tag}}}`).join("&")}`
          : "";
      const templateUrl = `${serviceURL("huddle")}template/${template.templateCode}/preview${params}`;
      linkDialog.handleOpen({ url: templateUrl });
      setLoading(false);
      return;
    }

    const response = await copyImage(getToken, asset?.public_url, uuidv4());
    if (!response.success) {
      toast.error("Failed to add image as link");
      return;
    }

    linkDialog.handleOpen({ url: response.url });
    setLoading(false);
  }, [linkDialog, asset, getToken, setLoading, onInsert, publishTags]);

  const handleLinkConfirm = useCallback(
    ({ text: title, url }: Link): void => {
      editor.dispatchCommand(INSERT_LINK_COMMAND, { title, url });
      linkDialog.handleClose();
      onClose();
    },
    [editor, linkDialog, onClose],
  );

  return (
    <>
      <Button
        data-analytics-id="editor-content-library-insert-link"
        type="button"
        disabled={disabled || asset === null}
        loading={loading}
        onClick={() => void handleInsertAssetAsLink()}
        data-testid="insert-upload-as-link-button"
      >
        Insert as link
      </Button>
      {linkDialog.open && linkDialog.data?.url && (
        <LinkForm
          data={{
            mode: "add",
            url: linkDialog.data.url,
            type: "asset",
          }}
          onCancel={linkDialog.handleClose}
          onConfirm={handleLinkConfirm}
          onRemove={() => {}}
          filterTags={() => ({})}
        />
      )}
    </>
  );
};
